var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isValidElement, Component, createRef, cloneElement, Children, Fragment, } from 'react';
import { Announcement, Table as AnvilTable, TableColumn, } from '@servicetitan/design-system';
import { operators as kendoOperators } from '@progress/kendo-react-grid/dist/npm/filterCommon';
import { GridPDFExport as TablePDFExport } from '@progress/kendo-react-pdf';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { observer } from 'mobx-react';
import { GRID_COL_INDEX_ATTRIBUTE } from '@progress/kendo-react-grid';
import { AsyncDataSource } from '@servicetitan/data-query';
import { SelectColumnCell, SelectHeaderCell, } from './select-cell/select-cell';
import memoizeOne from 'memoize-one';
import classNames from 'classnames';
import * as Styles from './table.module.css';
import { action, makeObservable, observable } from 'mobx';
import { useTdProps } from './utils/use-td-props';
const { text: textOperators, ...operators } = kendoOperators;
operators.text = [
    ...textOperators.filter(o => o.operator !== 'isnull' && o.operator !== 'isnotnull'),
];
function isColumnElement(element) {
    if (!isValidElement(element)) {
        return false;
    }
    const elementType = element.type;
    return elementType === TableColumn || elementType.originalType === TableColumn;
}
let Table = class Table extends Component {
    constructor(props) {
        super(props);
        Object.defineProperty(this, "observableScrollable", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "observableSelectionControl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "observableHideSelectAll", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "observableSelectable", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "tableState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: this.props.tableState
        });
        Object.defineProperty(this, "ref", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: createRef()
        });
        Object.defineProperty(this, "lastSelection", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "selectColumnCell", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observer((props) => (_jsx(SelectColumnCell, { ...props, isRowSelectable: this.tableState.isRowSelectable(props.dataItem), control: this.observableSelectionControl })))
        });
        Object.defineProperty(this, "customCellMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "selectHeaderCell", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: observer((props) => {
                if (this.observableHideSelectAll || this.tableState.selectionLimit !== Infinity) {
                    return null;
                }
                return (_jsx(SelectHeaderCell, { ...props, isSomeRowsSelected: this.observableScrollable !== 'virtual'
                        ? this.tableState.isSomePageRowsSelected
                        : this.tableState.isSomeRowsSelected, control: this.observableSelectionControl }));
            })
        });
        // TODO: rid of "memoizeOne" after migration on React.FC
        Object.defineProperty(this, "withCellTableState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: memoizeOne((children) => this.applyToColumns(children, column => {
                const { cell: Cell, field } = column.props;
                return cloneElement(column, {
                    cell: this.getOrCreateCellComponentWithTableState(Cell, field),
                });
            }))
        });
        Object.defineProperty(this, "rowRender", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (row, rowProps) => {
                const overrides = {};
                if (this.props.selectable &&
                    rowProps.rowType === 'data' &&
                    !this.tableState.isRowSelectable(rowProps.dataItem)) {
                    overrides.className = classNames(row.props.className, Styles.disabled);
                }
                const result = Object.keys(overrides).length ? cloneElement(row, overrides) : row;
                return this.props.rowRender ? this.props.rowRender(result, rowProps) : result;
            }
        });
        Object.defineProperty(this, "handleHeaderSelectionChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (ev) => {
                const checked = ev.syntheticEvent.currentTarget.checked;
                if (this.observableScrollable !== 'virtual') {
                    if (checked) {
                        this.tableState.selectPage();
                    }
                    else {
                        this.tableState.deselectPage();
                    }
                }
                else {
                    if (checked) {
                        this.tableState.selectAll();
                    }
                    else {
                        this.tableState.deselectAll();
                    }
                }
            }
        });
        Object.defineProperty(this, "handleSelectionChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (ev) => {
                var _a, _b, _c, _d;
                if (!ev.dataItem) {
                    return;
                }
                if (ev.nativeEvent.shiftKey && this.lastSelection) {
                    const rows = this.tableState
                        .getRowsBetween(this.lastSelection, ev.dataItem)
                        .filter(row => this.tableState.isRowSelectable(row));
                    const isAsc = ((_b = (_a = this.tableState.dataSource) === null || _a === void 0 ? void 0 : _a.idSelector) === null || _b === void 0 ? void 0 : _b.call(_a, this.lastSelection)) ===
                        ((_d = (_c = this.tableState.dataSource) === null || _c === void 0 ? void 0 : _c.idSelector) === null || _d === void 0 ? void 0 : _d.call(_c, rows[0]));
                    const selection = !ev.dataItem.selected;
                    if (selection) {
                        const toBeSelected = [];
                        for (const row of isAsc ? rows : rows.reverse()) {
                            if (this.tableState.selectedCount + toBeSelected.length >=
                                this.tableState.selectionLimit) {
                                break;
                            }
                            if (row.selected) {
                                continue;
                            }
                            toBeSelected.push(row);
                        }
                        this.tableState.setRowsSelection(toBeSelected, selection);
                    }
                    else {
                        this.tableState.setRowsSelection(rows, selection);
                    }
                }
                else {
                    this.tableState.toggleRowSelection(ev.dataItem);
                }
                this.lastSelection = ev.dataItem;
            }
        });
        Object.defineProperty(this, "scrollToBottom", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.ref.current) {
                    const container = this.ref.current.getElementsByClassName('k-grid-content')[0];
                    container.scrollTop = container.scrollHeight - container.clientHeight * 2;
                    container.scroll({ top: container.scrollHeight, behavior: 'smooth' });
                }
            }
        });
        Object.defineProperty(this, "table", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const { className, filterOperators, selectable, pager, ...props } = this.props;
                return (_jsxs(AnvilTable, { ref: this.ref, ...props, dataItemKey: this.tableState.rowIdKey, data: [...this.tableState.data], rowRender: this.rowRender, editField: "inEdit", filter: this.tableState.filter, filterOperators: filterOperators !== null && filterOperators !== void 0 ? filterOperators : operators, onFilterChange: this.tableState.handleFilterChange, group: [...this.tableState.group], onGroupChange: this.tableState.handleGroupChange, onExpandChange: this.tableState.handleExpandChange, expandField: "expanded", sort: [...this.tableState.sort], onSortChange: this.tableState.handleSortChange, onHeaderSelectionChange: this.handleHeaderSelectionChange, onSelectionChange: this.handleSelectionChange, selectedField: "selected", pageable: this.getPageable(), pageSize: this.tableState.pageSize, skip: this.tableState.skip, onPageChange: this.tableState.handlePageChange, total: this.tableState.filteredCount, className: classNames(Styles.table, className), selectable: { enabled: selectable, mode: 'multiple' }, children: [selectable && (_jsx(TableColumn, { locked: true, field: "selected", id: "selected", width: "50px", filterable: false, resizable: false, reorderable: false, headerSelectionValue: props.scrollable !== 'virtual'
                                ? this.tableState.isAllPageRowsSelected
                                : this.tableState.isAllRowsSelected, headerCell: this.selectHeaderCell, cell: this.selectColumnCell, headerClassName: "lh-1-i ta-center-i", className: classNames(Styles.unselectable, 'lh-1-i', 'ta-center-i') })), this.withCellTableState(props.children)] }));
            }
        });
        Object.defineProperty(this, "getOrCreateCellComponentWithTableState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (Cell, field) => {
                if (!Cell) {
                    return undefined;
                }
                const CellWithTableState = (props) => {
                    const tdProps = useTdProps(props);
                    return _jsx(Cell, { ...props, tableState: this.tableState, tdProps: tdProps });
                };
                CellWithTableState.displayName = 'CellWithTableState';
                if (field) {
                    if (this.customCellMap.has(field)) {
                        return this.customCellMap.get(field);
                    }
                    this.customCellMap.set(field, CellWithTableState);
                }
                return CellWithTableState;
            }
        });
        makeObservable(this);
        this.observableScrollable = this.props.scrollable;
        this.observableSelectionControl = this.props.selectionControl;
        this.observableHideSelectAll = this.props.hideSelectAll;
    }
    componentDidUpdate() {
        this.observableScrollable = this.props.scrollable;
        this.observableSelectionControl = this.props.selectionControl;
        this.observableHideSelectAll = this.props.hideSelectAll;
    }
    applyToColumns(children, transformer) {
        return Children.map(children, child => {
            if (!isColumnElement(child)) {
                return child;
            }
            return child.props.children
                ? cloneElement(transformer(child), {
                    children: this.applyToColumns(child.props.children, transformer),
                })
                : transformer(child);
        });
    }
    render() {
        if (this.tableState.dataSource instanceof AsyncDataSource &&
            this.props.scrollable === 'virtual' &&
            this.props.selectable) {
            return (_jsx(Announcement, { status: "critical", title: "AsyncDataSource doesn't support virtual scroll with selection." }));
        }
        const table = this.table();
        return (_jsxs(Fragment, { children: [table, this.props.exportable && (_jsxs(Fragment, { children: [_jsxs(TablePDFExport, { ref: this.tableState.setTablePdfExportRef, fileName: sanitizeExportFileName(this.props.exportFileName), children: [this.props.children, table] }), _jsx(ExcelExport, { ref: this.tableState.setTableExcelExportRef, fileName: sanitizeExportFileName(this.props.exportFileName), children: this.props.children })] }))] }));
    }
    getPageable() {
        var _a;
        if (this.props.scrollable === 'virtual' || !this.tableState.pageSize) {
            return false;
        }
        return (_a = this.props.pager) !== null && _a !== void 0 ? _a : true;
    }
};
__decorate([
    observable,
    __metadata("design:type", Object)
], Table.prototype, "observableScrollable", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Table.prototype, "observableSelectionControl", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Table.prototype, "observableHideSelectAll", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Table.prototype, "observableSelectable", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Table.prototype, "componentDidUpdate", null);
Table = __decorate([
    observer,
    __metadata("design:paramtypes", [Object])
], Table);
export { Table };
const sanitizeExportFileName = memoizeOne((fileName) => fileName === null || fileName === void 0 ? void 0 : fileName.replace(/\.+/g, '_'));
//# sourceMappingURL=table.js.map